/**
 * API client for Task List feature.
 */

import api from './api';
import { Task, TaskRecurrence, CreateTaskFromMessageRequest, TaskFilters } from '../types/task';

export const tasksApi = {
  /**
   * Get all tasks for the current user.
   */
  getTasks: async (filters?: TaskFilters): Promise<{ results: Task[]; count: number }> => {
    const params = new URLSearchParams();
    
    if (filters?.status) {
      params.append('status', filters.status);
    }
    if (filters?.priority) {
      params.append('priority', filters.priority);
    }
    if (filters?.due_before) {
      params.append('due_before', filters.due_before);
    }
    if (filters?.due_after) {
      params.append('due_after', filters.due_after);
    }
    
    const queryString = params.toString();
    const url = `/tasks/tasks/${queryString ? `?${queryString}` : ''}`;
    
    const response = await api.get<{ results: Task[]; count: number } | Task[]>(url);
    // Handle both paginated and non-paginated responses
    if (Array.isArray(response)) {
      return { results: response, count: response.length };
    }
    if (response && typeof response === 'object' && 'results' in response) {
      return response as { results: Task[]; count: number };
    }
    // Fallback: return empty results
    return { results: [], count: 0 };
  },

  /**
   * Get a single task by ID.
   */
  getTask: async (id: string): Promise<Task> => {
    const response = await api.get<Task>(`/tasks/tasks/${id}/`);
    return response;
  },

  /**
   * Create a new task.
   */
  createTask: async (task: Partial<Task>): Promise<Task> => {
    const response = await api.post<Task>('/tasks/tasks/', task);
    return response;
  },

  /**
   * Update a task.
   */
  updateTask: async (id: string, task: Partial<Task>): Promise<Task> => {
    const response = await api.patch<Task>(`/tasks/tasks/${id}/`, task);
    return response;
  },

  /**
   * Delete a task.
   */
  deleteTask: async (id: string): Promise<void> => {
    await api.delete(`/tasks/tasks/${id}/`);
  },

  /**
   * Mark a task as completed.
   */
  completeTask: async (id: string): Promise<Task> => {
    const response = await api.post<Task>(`/tasks/tasks/${id}/complete/`);
    return response;
  },

  /**
   * Archive a task.
   */
  archiveTask: async (id: string): Promise<Task> => {
    const response = await api.post<Task>(`/tasks/tasks/${id}/archive/`);
    return response;
  },

  /**
   * Get upcoming tasks (due in next hour).
   */
  getUpcomingTasks: async (): Promise<Task[]> => {
    const response = await api.get<Task[]>('/tasks/tasks/upcoming/');
    return response;
  },

  /**
   * Create a task from a message.
   */
  createTaskFromMessage: async (request: CreateTaskFromMessageRequest): Promise<Task> => {
    const response = await api.post<Task>('/tasks/tasks/from_message/', request);
    return response;
  },
};
